/**
 * One vocabulary for "is this maintenance item still live".
 *
 * The twin of `MaintenanceIssue`'s constants on the PHP side. Both had
 * been spelled out as literals in about twenty places, which read as a
 * contradiction and is not one: there are two QUESTIONS here, and most
 * of the call sites were answering the right one without ever saying
 * which. Name the question, then pick the list.
 *
 * There is no `closed` status. Three filters excluded one anyway; the
 * exclusion never matched a row, and what it actually did was make
 * those filters LOOK like OUTSTANDING while behaving as UNFINISHED.
 */

export const STATUS_OPEN = 'open';
export const STATUS_IN_PROGRESS = 'in_progress';
export const STATUS_RESOLVED = 'resolved';
export const STATUS_PENDING_REVIEW = 'pending_review';

/** Every status a row may actually hold. */
export const STATUSES = [
    STATUS_OPEN,
    STATUS_IN_PROGRESS,
    STATUS_RESOLVED,
    STATUS_PENDING_REVIEW,
];

/**
 * OUTSTANDING WORK — what somebody still has to go and do.
 *
 * `pending_review` is deliberately absent: it is a submission awaiting
 * triage, not work the org has accepted. This is what the Issues list's
 * "Unfinished" chip, its count badge and every planning surface mean.
 */
export const OUTSTANDING_STATUSES = [STATUS_OPEN, STATUS_IN_PROGRESS];

/**
 * NOT FINISHED — outstanding work plus the triage queue.
 *
 * Use it where hiding a row that still exists is the worse error:
 * search, pickers, duplicate detection, the offline pack, and the home
 * strip.
 */
export const UNFINISHED_STATUSES = [
    STATUS_OPEN,
    STATUS_IN_PROGRESS,
    STATUS_PENDING_REVIEW,
];

/**
 * SETTABLE — what a member may move this row TO.
 *
 * Not a third answer to the question above: the other two decide which
 * rows a list SHOWS, this one decides which values a write may carry,
 * and it is what a status picker offers. `pending_review` is absent
 * because triage is one-way — approving a submission makes it `open`.
 *
 * Must stay in step with `MaintenanceIssue::SETTABLE_STATUSES`, which
 * is what the server validates against: a picker offering a value the
 * rule refuses is a control that 422s.
 */
export const SETTABLE_STATUSES = [
    STATUS_OPEN,
    STATUS_IN_PROGRESS,
    STATUS_RESOLVED,
];

/** Is there still work to do on this row? */
export const isOutstanding = (status) => OUTSTANDING_STATUSES.includes(status);

/** Is this row anything other than finished? */
export const isUnfinished = (status) => UNFINISHED_STATUSES.includes(status);

/** May a member move a row to this status? */
export const isSettable = (status) => SETTABLE_STATUSES.includes(status);
